/**
 * VitalsSection - Character vital stats as a collapsible section
 *
 * Renders HP, XP, ability scores, combat stats, conditions, etc.
 * inside a SectionWrapper accordion. Used by CharDetailsPanel in both
 * lobby and game-session contexts.
 */

import React from 'react';
import {
  Heart,
  Shield,
  Zap,
  Footprints,
  Eye,
  Dices,
  Target,
  Sparkles,
  AlertTriangle,
  Sword,
  Wind,
  HeartPulse,
  Brain,
  Compass,
  Sun,
  Star,
  Flame,
  Scale,
  Leaf,
  Shuffle,
  Crown,
  Skull,
  Bomb,
  CircleDot,
} from 'lucide-react';
import { ConcentrationPill } from '@/components/shared/ConcentrationPill';
import type { LucideIcon } from 'lucide-react';
import { ConditionBadge } from '@/components/ui/condition-badge';
import { HpBar } from '@/components/ui/hp-bar';
import { SectionWrapper } from './CharDetailsSections';
import { cn } from '@/lib/utils';
import type { VitalsData } from './vitals-utils';

// ─── AbilityScore sub-component ───────────────────────────────

const ABILITY_ICON: Record<string, { Icon: LucideIcon; color: string }> = {
  STR: { Icon: Sword, color: 'text-red-500' },
  DEX: { Icon: Wind, color: 'text-emerald-500' },
  CON: { Icon: HeartPulse, color: 'text-amber-600' },
  INT: { Icon: Brain, color: 'text-blue-500' },
  WIS: { Icon: Compass, color: 'text-teal-500' },
  CHA: { Icon: Sparkles, color: 'text-yellow-500' },
};

const AbilityScore = React.memo(function AbilityScore({
  label,
  score,
}: {
  label: string;
  score: number;
}) {
  const mod = Math.floor((score - 10) / 2);
  const modStr = mod >= 0 ? `+${mod}` : `${mod}`;
  const { Icon, color } = ABILITY_ICON[label] ?? { Icon: Sword, color: 'text-muted-foreground' };

  return (
    <div className="pt-2 p-1 bg-muted/30 rounded flex flex-col items-center">
      <Icon className={`h-4 w-4 mb-1 ${color}`} />
      <div className="text-sm font-bold">{score}</div>
      <div className="text-[10px] text-muted-foreground">{mod !== 0 ? modStr : '\u00A0'}</div>
      <div className="text-[10px] text-muted-foreground font-medium">{label}</div>
    </div>
  );
});

// ─── Alignment icon/color mapping ────────────────────────────

const ALIGNMENT_MAP: Record<string, { Icon: LucideIcon; color: string; label: string }> = {
  lawful_good: { Icon: Sun, color: 'text-amber-400', label: 'Lawful Good' },
  neutral_good: { Icon: Star, color: 'text-rose-400', label: 'Neutral Good' },
  chaotic_good: { Icon: Flame, color: 'text-orange-400', label: 'Chaotic Good' },
  lawful_neutral: { Icon: Scale, color: 'text-sky-400', label: 'Lawful Neutral' },
  true_neutral: { Icon: Leaf, color: 'text-emerald-400', label: 'True Neutral' },
  chaotic_neutral: { Icon: Shuffle, color: 'text-violet-400', label: 'Chaotic Neutral' },
  lawful_evil: { Icon: Crown, color: 'text-red-700', label: 'Lawful Evil' },
  neutral_evil: { Icon: Skull, color: 'text-red-500', label: 'Neutral Evil' },
  chaotic_evil: { Icon: Bomb, color: 'text-red-400', label: 'Chaotic Evil' },
  unaligned: { Icon: CircleDot, color: 'text-gray-400', label: 'Unaligned' },
};

function getAlignmentInfo(alignment: string) {
  const key = alignment.toLowerCase().replace(/\s+/g, '_');
  return ALIGNMENT_MAP[key] ?? { Icon: CircleDot, color: 'text-gray-400', label: alignment };
}

function formatSignedBonus(bonus: number): string {
  return bonus >= 0 ? `+${bonus}` : `${bonus}`;
}

// ─── VitalsSection component ──────────────────────────────────

interface VitalsSectionProps {
  data: VitalsData;
  expanded: boolean;
  onToggle: () => void;
  showSeparator: boolean;
}

export function VitalsSection({ data, expanded, onToggle, showSeparator }: VitalsSectionProps) {
  const alignInfo = data.alignment ? getAlignmentInfo(data.alignment) : null;
  const AlignIcon = alignInfo?.Icon;

  return (
    <SectionWrapper
      title={
        <>
          <Heart className="h-4 w-4 text-red-500" />
          Vitals
        </>
      }
      expanded={expanded}
      onToggle={onToggle}
      showSeparator={showSeparator}
    >
      <div className="space-y-3">
        {/* HP Bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1">
              <Heart className="h-3 w-3 text-red-500" />
              HP
            </span>
            <span className="font-medium">
              {data.hpCurrent}/{data.hpMax}
            </span>
          </div>
          <HpBar current={data.hpCurrent} max={data.hpMax} />
        </div>

        {/* XP Bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className={cn('flex items-center gap-1', data.canLevelUp && 'animate-pulse')}>
              <Zap
                className={cn('h-3 w-3', data.canLevelUp ? 'text-yellow-400' : 'text-amber-500')}
              />
              XP
              {data.canLevelUp && <Sparkles className="h-3 w-3 text-yellow-400" />}
            </span>
            <span className={cn('font-medium', data.canLevelUp && 'text-yellow-400')}>
              {data.xpCurrent}/{data.xpNext}
            </span>
          </div>
          <div
            className={cn(
              'h-2 bg-muted rounded-full overflow-hidden',
              data.canLevelUp && 'animate-pulse ring-1 ring-yellow-400/50'
            )}
          >
            <div
              className="h-full transition-all bg-gradient-to-r from-sky-300 to-blue-600"
              style={{ width: `${Math.max(0, Math.min(100, data.xpPercent))}%` }}
            />
          </div>
        </div>

        {/* Combat Stats: AC, Init, Speed */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 bg-muted/50 rounded-lg">
            <Shield className="h-4 w-4 mx-auto mb-1 text-blue-500" />
            <div className="text-lg font-bold">{data.ac}</div>
            <div className="text-xs text-muted-foreground">AC</div>
          </div>
          <div className="p-2 bg-muted/50 rounded-lg">
            <Zap className="h-4 w-4 mx-auto mb-1 text-amber-500" />
            <div className="text-lg font-bold">{formatSignedBonus(data.initiativeBonus)}</div>
            <div className="text-xs text-muted-foreground">INIT</div>
          </div>
          <div className="p-2 bg-muted/50 rounded-lg">
            <Footprints className="h-4 w-4 mx-auto mb-1 text-green-500" />
            <div className="text-lg font-bold">{data.speed}</div>
            <div className="text-xs text-muted-foreground">SPEED</div>
          </div>
        </div>

        {/* Ability Scores */}
        <div className="grid grid-cols-6 gap-1 text-center">
          <AbilityScore label="STR" score={data.str} />
          <AbilityScore label="DEX" score={data.dex} />
          <AbilityScore label="CON" score={data.con} />
          <AbilityScore label="INT" score={data.int} />
          <AbilityScore label="WIS" score={data.wis} />
          <AbilityScore label="CHA" score={data.cha} />
        </div>

        {/* Secondary Stats Row */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 bg-muted/50 rounded-lg">
            <Dices className="h-4 w-4 mx-auto mb-1 text-purple-500" />
            <div className="text-lg font-bold">+{data.proficiencyBonus}</div>
            <div className="text-xs text-muted-foreground">PROF</div>
          </div>
          <div className="p-2 bg-muted/50 rounded-lg">
            <Heart className="h-4 w-4 mx-auto mb-1 text-pink-500" />
            <div className="text-lg font-bold">{data.hitDie}</div>
            <div className="text-xs text-muted-foreground">HIT DIE</div>
          </div>
          <div className="p-2 bg-muted/50 rounded-lg">
            <Eye className="h-4 w-4 mx-auto mb-1 text-cyan-500" />
            <div className="text-lg font-bold">{data.passivePerception}</div>
            <div className="text-xs text-muted-foreground">PERC</div>
          </div>
        </div>

        {/* Spellcasting Stats + Alignment */}
        {(data.spellcasting || alignInfo) && (
          <div
            className={cn(
              'grid gap-2 text-center',
              data.spellcasting && alignInfo
                ? 'grid-cols-3'
                : data.spellcasting
                  ? 'grid-cols-2'
                  : 'grid-cols-1'
            )}
          >
            {data.spellcasting && (
              <div className="p-2 bg-muted/50 rounded-lg">
                <Target className="h-4 w-4 mx-auto mb-1 text-orange-500" />
                <div className="text-lg font-bold">{data.spellcasting.spell_save_dc}</div>
                <div className="text-xs text-muted-foreground">SPELL DC</div>
              </div>
            )}
            {alignInfo && AlignIcon && (
              <div className="p-2 bg-muted/50 rounded-lg">
                <AlignIcon className={cn('h-4 w-4 mx-auto mb-1', alignInfo.color)} />
                <div className="text-sm font-bold">{alignInfo.label}</div>
                <div className="text-xs text-muted-foreground">ALIGN</div>
              </div>
            )}
            {data.spellcasting && (
              <div className="p-2 bg-muted/50 rounded-lg">
                <Sparkles className="h-4 w-4 mx-auto mb-1 text-violet-500" />
                <div className="text-lg font-bold">+{data.spellcasting.spell_attack_bonus}</div>
                <div className="text-xs text-muted-foreground">SPELL ATK</div>
              </div>
            )}
          </div>
        )}

        {/* Senses (Darkvision) */}
        {!!data.darkvision && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Eye className="h-3 w-3 text-violet-400" />
            <span>Darkvision {data.darkvision} ft.</span>
          </div>
        )}

        {/* Temp HP (if any) */}
        {data.tempHp > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Shield className="h-3 w-3 text-cyan-500" />
            <span className="text-cyan-500 font-medium">+{data.tempHp} Temp HP</span>
          </div>
        )}

        {/* Concentration (if any) — shared pill, friendly-gated server-side */}
        {data.isConcentrating && (
          <div className="flex items-center gap-2 text-sm">
            <ConcentrationPill
              isConcentrating={data.isConcentrating}
              spellName={data.concentrationSpell}
            />
          </div>
        )}

        {/* Conditions (if any) */}
        {data.conditions.length > 0 && (
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <AlertTriangle className="h-3 w-3" />
              <span>Conditions</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {data.conditions.map((condition) => {
                const condName = typeof condition === 'string' ? condition : condition.name;
                return <ConditionBadge key={condName} condition={condition} />;
              })}
            </div>
          </div>
        )}

        {/* Death Saves (if down) */}
        {(data.deathSaveSuccesses !== undefined || data.deathSaveFailures !== undefined) &&
          data.hpCurrent <= 0 && (
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Death Saves</div>
              <div className="flex gap-2 text-sm">
                <span className="text-green-500">Successes: {data.deathSaveSuccesses ?? 0}/3</span>
                <span className="text-red-500">Failures: {data.deathSaveFailures ?? 0}/3</span>
              </div>
            </div>
          )}
      </div>
    </SectionWrapper>
  );
}
